import React, { useState, useEffect } from 'react';
import { 
  Dog, 
  Clock, 
  AlertCircle,
  CheckCircle,
  ChevronUp,
  ChevronDown,
  Star,
  BarChart,
  TrendingUp,
  Calendar,
  DollarSign
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, API_ENDPOINTS, getAuthHeaders } from '../config/api.ts';

interface DashboardStats {
  totalOccupancy: number;
  checkInsToday: number;
  checkOutsToday: number;
  activitiesCount: number;
  pendingShares: number;
  occupancyRate: number;
}

interface SurveyStats {
  averageRating: number;
  totalReviews: number;
  recentReviews: Array<{
    rating: number;
    feedback: string;
    date: string;
  }>;
}

interface WeeklySummary {
  totalVisitors: number;
  averageStay: number;
  weeklyRevenue: number;
  customerRating: number;
  percentageChange: number;
}

interface DailyOccupancy {
  day: string;
  count: number;
  percentage: number;
}

interface Achievement {
  icon: string;
  title: string;
  value: string;
  date?: string;
}

interface TodayReservation {
  _id: string;
  customer: {
    name: string;
    phone: string;
  };
  dog: {
    name: string;
    breed: string;
  };
  checkInDate: string;
  checkOutDate: string;
  status: string;
}

const NewDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalOccupancy: 0,
    checkInsToday: 0,
    checkOutsToday: 0,
    activitiesCount: 0,
    pendingShares: 0,
    occupancyRate: 0
  });
  const [todayReservations, setTodayReservations] = useState<{
    checkIns: TodayReservation[];
    checkOuts: TodayReservation[];
    currentGuests: TodayReservation[];
  }>({
    checkIns: [],
    checkOuts: [],
    currentGuests: []
  });
  const [surveyStats, setSurveyStats] = useState<SurveyStats>({
    averageRating: 0,
    totalReviews: 0,
    recentReviews: []
  });
  const [weeklySummary, setWeeklySummary] = useState<WeeklySummary>({
    totalVisitors: 0,
    averageStay: 0,
    weeklyRevenue: 0,
    customerRating: 0,
    percentageChange: 0
  });
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) return;

      const headers = getAuthHeaders(token);

      // Bugünkü check-in/check-out'ları çek (checkins koleksiyonu - gerçek veriler var)
      const reservationsResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CHECKINS_TODAY}`, {
        headers
      });
      const reservationsData = await reservationsResponse.json();

      if (reservationsData.success) {
        setTodayReservations(reservationsData.data);
        setStats(prev => ({
          ...prev,
          totalOccupancy: reservationsData.data.currentGuests.length,
          checkInsToday: reservationsData.data.checkIns.length,
          checkOutsToday: reservationsData.data.checkOuts.length,
          occupancyRate: Math.round((reservationsData.data.currentGuests.length / 50) * 100)
        }));
      }

      // Bugünkü aktiviteleri çek
      const activitiesResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ACTIVITIES_TODAY}`, {
        headers
      });
      const activitiesData = await activitiesResponse.json();

      if (activitiesData.success) {
        setStats(prev => ({
          ...prev,
          activitiesCount: activitiesData.data.stats.total,
          pendingShares: activitiesData.data.stats.pending
        }));
      }

      // Anket istatistiklerini çek
      await fetchSurveyStats(token);

      // Haftalık özeti hesapla
      await fetchWeeklySummary(token);

      // Başarıları hesapla
      await fetchAchievements(token);

    } catch (error) {
      // Dashboard data fetch error
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveyStats = async (token: string) => {
    try {
      const headers = getAuthHeaders(token);
      const response = await fetch(`${API_BASE_URL}/api/surveys/admin/results?page=1&limit=50&type=hotel_service`, {
        headers
      });

      const data = await response.json();
      
      if (data.success && data.data.surveys) {
        const surveys = data.data.surveys;
        
        // Ortalama rating hesapla
        const ratingsSum = surveys.reduce((sum: number, survey: any) => 
          sum + (survey.anonymizedResponses?.overallRating || 0), 0
        );
        const averageRating = surveys.length > 0 ? ratingsSum / surveys.length : 0;

        // Son 3 yorumu al
        const recentReviews = surveys
          .filter((s: any) => s.anonymizedResponses?.feedback)
          .slice(0, 3)
          .map((s: any) => ({
            rating: s.anonymizedResponses.overallRating || 0,
            feedback: s.anonymizedResponses.feedback || '',
            date: s.completedAt
          }));

        setSurveyStats({
          averageRating: Math.round(averageRating * 10) / 10,
          totalReviews: surveys.length,
          recentReviews
        });
      }
    } catch (error) {
      // Survey stats error
    }
  };

  const fetchWeeklySummary = async (token: string) => {
    try {
      const headers = getAuthHeaders(token);
      
      // Son 7 günlük tamamlanmış check-in'leri al
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CHECKIN_COMPLETED_STAFF}`, {
        headers
      });

      // Check for token expiration
      if (response.status === 401) {
        const { handleTokenExpired } = await import('../config/api');
        handleTokenExpired(response);
        return;
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        const completedCheckins = data.data;
        
        // Son 7 günü filtrele
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        
        const thisWeekCheckins = completedCheckins.filter((checkin: any) => {
          const checkoutDate = new Date(checkin.checkOutTime);
          return checkoutDate >= oneWeekAgo;
        });

        // Unique köpek sayısı
        const uniqueDogs = new Set(thisWeekCheckins.map((c: any) => c.dog?._id).filter(Boolean));
        
        // Ortalama kalış süresi (saat cinsinden)
        const totalHours = thisWeekCheckins.reduce((sum: number, checkin: any) => {
          if (checkin.duration) {
            return sum + checkin.duration;
          }
          return sum;
        }, 0);
        const averageStay = thisWeekCheckins.length > 0 ? totalHours / thisWeekCheckins.length : 0;

        // Geçen haftayla karşılaştırma
        const twoWeeksAgo = new Date();
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
        
        const lastWeekCheckins = completedCheckins.filter((checkin: any) => {
          const checkoutDate = new Date(checkin.checkOutTime);
          return checkoutDate >= twoWeeksAgo && checkoutDate < oneWeekAgo;
        });

        const percentageChange = lastWeekCheckins.length > 0 
          ? Math.round(((thisWeekCheckins.length - lastWeekCheckins.length) / lastWeekCheckins.length) * 100)
          : 0;

        setWeeklySummary({
          totalVisitors: uniqueDogs.size,
          averageStay: Math.round(averageStay * 10) / 10,
          weeklyRevenue: 0, // Artık kullanılmıyor
          customerRating: surveyStats.averageRating,
          percentageChange
        });
      }
    } catch (error) {
      // Weekly summary error
    }
  };

  const fetchAchievements = async (token: string) => {
    try {
      const headers = getAuthHeaders(token);
      
      // Tamamlanmış check-in'leri al
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CHECKIN_COMPLETED_STAFF}`, {
        headers
      });

      // Check for token expiration
      if (response.status === 401) {
        const { handleTokenExpired } = await import('../config/api');
        handleTokenExpired(response);
        return;
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        const completedCheckins = data.data;
        
        // Toplam check-in sayısı
        const totalCheckIns = completedCheckins.length;

        // En uzun kalış
        const longestStay = completedCheckins.reduce((max: any, checkin: any) => {
          return (checkin.duration || 0) > (max.duration || 0) ? checkin : max;
        }, { duration: 0 });

        // En sadık müşteri (en çok check-in yapan)
        const ownerCounts: { [key: string]: { count: number; name: string } } = {};
        completedCheckins.forEach((checkin: any) => {
          if (checkin.dog?.owner) {
            const ownerId = checkin.dog.owner._id;
            const ownerName = `${checkin.dog.owner.name || ''} ${checkin.dog.owner.surname || ''}`.trim();
            if (!ownerCounts[ownerId]) {
              ownerCounts[ownerId] = { count: 0, name: ownerName };
            }
            ownerCounts[ownerId].count++;
          }
        });
        
        const mostLoyalCustomer = Object.values(ownerCounts).reduce((max, current) => 
          current.count > max.count ? current : max, { count: 0, name: '' }
        );

        // Aktivite sayısını al
        const activitiesResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.DOG_STATUS_BRANCH}?page=1&limit=1000`, {
          headers
        });
        const activitiesData = await activitiesResponse.json();
        const totalActivities = activitiesData.success ? activitiesData.data.length : 0;

        const achievementsList: Achievement[] = [
          {
            icon: '🎯',
            title: 'Toplam Tamamlanan Ziyaret',
            value: `${totalCheckIns} ziyaret`,
            date: 'Başarıyla tamamlandı'
          },
          {
            icon: '⏱️',
            title: 'En Uzun Kalış Rekoru',
            value: longestStay.duration ? `${Math.floor(longestStay.duration / 24)} gün ${longestStay.duration % 24} saat` : '-',
            date: longestStay.dog?.name || ''
          },
          {
            icon: '🏆',
            title: 'En Sadık Müşteri',
            value: `${mostLoyalCustomer.count} ziyaret`,
            date: mostLoyalCustomer.name
          },
          {
            icon: '⚡',
            title: 'Son 3 Günlük Aktivite Güncellemesi',
            value: `${totalActivities} güncelleme`,
            date: 'Staff tarafından eklendi'
          }
        ];

        setAchievements(achievementsList);
      }
    } catch (error) {
      // Achievements error
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // Her 15 dakikada bir güncelle (5 dakikadan 15 dakikaya çıkarıldı)
    const interval = setInterval(fetchDashboardData, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={20}
            className={`${
              star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const formatAverageStay = (hours: number) => {
    if (!hours || hours <= 0) {
      return '0 saat';
    }

    const roundedHours = Math.round(hours);
    const days = Math.floor(roundedHours / 24);
    const remainingHours = roundedHours % 24;

    const parts: string[] = [];
    if (days > 0) {
      parts.push(`${days} gün`);
    }
    if (remainingHours > 0 || parts.length === 0) {
      parts.push(`${remainingHours} saat`);
    }

    return parts.join(' ');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Dashboard yükleniyor...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Günlük özet ve önemli bilgiler</p>
        </div>
      </div>

      {/* Stats Cards - Premium Design */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Mevcut Doluluk */}
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-600 via-slate-700 to-slate-800 rounded-2xl shadow-xl p-6 transform transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl group">
          {/* Animated Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
          </div>

          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg ring-2 ring-white/20 group-hover:ring-white/30 transition-all">
                <Dog className="text-white" size={28} />
              </div>
              <div className="flex flex-col items-end">
                <div className="text-5xl font-black text-white drop-shadow-lg">{stats.totalOccupancy}</div>
                <div className="text-xs text-white/70 font-medium uppercase tracking-wider mt-1">Köpek</div>
              </div>
            </div>
            <div className="border-t border-white/10 pt-3">
              <p className="text-white/95 text-sm font-semibold">Mevcut Doluluk</p>
              <p className="text-white/60 text-xs mt-1">Şu anda otelde aktif</p>
            </div>
          </div>
        </div>

        {/* Bugünkü Check-in'ler */}
        <div className="relative overflow-hidden bg-gradient-to-br from-teal-500 via-teal-600 to-cyan-700 rounded-2xl shadow-xl p-6 transform transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl group">
          {/* Animated Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
          </div>

          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg ring-2 ring-white/20 group-hover:ring-white/30 transition-all">
                <CheckCircle className="text-white" size={28} />
              </div>
              <div className="flex flex-col items-end">
                <div className="text-5xl font-black text-white drop-shadow-lg">{stats.checkInsToday}</div>
                <div className="flex items-center space-x-1 mt-1">
                  <ChevronUp className="text-white/70" size={16} />
                  <div className="text-xs text-white/70 font-medium uppercase tracking-wider">Giriş</div>
                </div>
              </div>
            </div>
            <div className="border-t border-white/10 pt-3">
              <p className="text-white/95 text-sm font-semibold">Bugünkü Check-in</p>
              <p className="text-white/60 text-xs mt-1">Yeni gelen köpekler</p>
            </div>
          </div>
        </div>

        {/* Bugünkü Check-out'lar */}
        <div className="relative overflow-hidden bg-gradient-to-br from-rose-400 via-pink-500 to-fuchsia-600 rounded-2xl shadow-xl p-6 transform transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl group">
          {/* Animated Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
          </div>

          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg ring-2 ring-white/20 group-hover:ring-white/30 transition-all">
                <Clock className="text-white" size={28} />
              </div>
              <div className="flex flex-col items-end">
                <div className="text-5xl font-black text-white drop-shadow-lg">{stats.checkOutsToday}</div>
                <div className="flex items-center space-x-1 mt-1">
                  <ChevronDown className="text-white/70" size={16} />
                  <div className="text-xs text-white/70 font-medium uppercase tracking-wider">Çıkış</div>
                </div>
              </div>
            </div>
            <div className="border-t border-white/10 pt-3">
              <p className="text-white/95 text-sm font-semibold">Bugünkü Check-out</p>
              <p className="text-white/60 text-xs mt-1">Eve dönen köpekler</p>
            </div>
          </div>
        </div>

      </div>

      {/* Today's Check-ins & Check-outs - Premium Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Check-ins */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-5 bg-gradient-to-r from-teal-600 to-cyan-700">
            <h3 className="text-lg font-bold text-white flex items-center">
              <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center mr-3 ring-2 ring-white/20">
                <CheckCircle className="text-white" size={20} />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <span>Bugünkü Check-in'ler</span>
                  <span className="bg-white/15 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-bold">
                    {stats.checkInsToday}
                  </span>
                </div>
                <p className="text-xs text-white/70 font-normal mt-0.5">Yeni misafirlerimiz</p>
              </div>
            </h3>
          </div>
          <div className="p-6 bg-gradient-to-b from-gray-50 to-white">
            {todayReservations.checkIns.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-inner">
                  <CheckCircle className="text-gray-400" size={32} />
                </div>
                <p className="text-gray-500 font-medium">Bugün check-in yok</p>
                <p className="text-gray-400 text-sm mt-1">Yeni giriş beklenmiyor</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto custom-scrollbar">
                {todayReservations.checkIns.map((reservation) => (
                  <div key={reservation._id} className="group">
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 group-hover:border-teal-300 group-hover:shadow-md transition-all duration-300">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-xl flex items-center justify-center shadow-sm">
                          <Dog className="text-white" size={24} />
                        </div>
                        <div>
                          <p className="font-bold text-gray-900 text-base">{reservation.dog.name}</p>
                          <p className="text-sm text-gray-600 font-medium">{reservation.customer.name}</p>
                          <p className="text-xs text-gray-500 bg-gray-100 inline-block px-2 py-0.5 rounded-full mt-1">{reservation.dog.breed}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center justify-end space-x-1 mb-2">
                          <Clock className="text-teal-600" size={14} />
                          <p className="text-sm font-bold text-teal-600">
                            {formatTime(reservation.checkInDate)}
                          </p>
                        </div>
                        <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-teal-100 text-teal-700 border border-teal-200">
                          {reservation.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Check-outs */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-5 bg-gradient-to-r from-rose-500 to-pink-600">
            <h3 className="text-lg font-bold text-white flex items-center">
              <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center mr-3 ring-2 ring-white/20">
                <Clock className="text-white" size={20} />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <span>Bugünkü Check-out'lar</span>
                  <span className="bg-white/15 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-bold">
                    {stats.checkOutsToday}
                  </span>
                </div>
                <p className="text-xs text-white/70 font-normal mt-0.5">Eve dönenler</p>
              </div>
            </h3>
          </div>
          <div className="p-6 bg-gradient-to-b from-gray-50 to-white">
            {todayReservations.checkOuts.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-inner">
                  <Clock className="text-gray-400" size={32} />
                </div>
                <p className="text-gray-500 font-medium">Bugün check-out yok</p>
                <p className="text-gray-400 text-sm mt-1">Çıkış beklenmiyor</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto custom-scrollbar">
                {todayReservations.checkOuts.map((reservation) => (
                  <div key={reservation._id} className="group">
                    <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 group-hover:border-rose-300 group-hover:shadow-md transition-all duration-300">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-rose-400 to-pink-500 rounded-xl flex items-center justify-center shadow-sm">
                          <Dog className="text-white" size={24} />
                        </div>
                        <div>
                          <p className="font-bold text-gray-900 text-base">{reservation.dog.name}</p>
                          <p className="text-sm text-gray-600 font-medium">{reservation.customer.name}</p>
                          <p className="text-xs text-gray-500 bg-gray-100 inline-block px-2 py-0.5 rounded-full mt-1">{reservation.dog.breed}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center justify-end space-x-1 mb-2">
                          <Clock className="text-rose-600" size={14} />
                          <p className="text-sm font-bold text-rose-600">
                            {formatTime(reservation.checkOutDate)}
                          </p>
                        </div>
                        <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-rose-100 text-rose-700 border border-rose-200">
                          Check-out
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Müşteri Memnuniyeti - Premium Design */}
      <div 
        className="relative overflow-hidden bg-gradient-to-br from-amber-500/90 via-orange-500/90 to-rose-500/90 rounded-2xl shadow-lg p-8 cursor-pointer transform transition-all duration-500 hover:scale-[1.01] hover:shadow-xl group"
        onClick={() => navigate('/anket')}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full -ml-24 -mb-24"></div>
        </div>

        {/* Content */}
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-14 h-14 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-md ring-2 ring-white/20">
                <Star className="text-white" size={28} fill="white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white drop-shadow-md">Müşteri Memnuniyeti</h3>
                <p className="text-white/80 text-sm font-medium flex items-center space-x-1">
                  <span>Detaylar için tıklayın</span>
                  <ChevronUp className="group-hover:translate-x-1 transition-transform" size={16} />
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Sol Taraf - Rating */}
            <div className="bg-white/95 backdrop-blur-md rounded-2xl p-6 shadow-lg">
              <div className="text-center">
                <div className="text-7xl font-black text-transparent bg-clip-text bg-gradient-to-br from-amber-600 to-orange-600 mb-3">
                  {surveyStats.averageRating.toFixed(1)}
                </div>
                <div className="flex justify-center mb-3 space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      size={24}
                      className={`${
                        star <= Math.round(surveyStats.averageRating) 
                          ? 'text-amber-400 fill-amber-400' 
                          : 'text-gray-300'
                      } transition-all duration-300`}
                    />
                  ))}
                </div>
                <p className="text-sm font-semibold text-gray-600">5 üzerinden mükemmel puan!</p>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Toplam Değerlendirme</p>
                  <p className="text-2xl font-bold text-amber-600">{surveyStats.totalReviews}</p>
                </div>
              </div>
            </div>

            {/* Sağ Taraf - Son Yorumlar */}
            <div className="bg-white/95 backdrop-blur-md rounded-2xl p-6 shadow-lg">
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-xl mr-2">💬</span>
                Son Yorumlar
              </h4>
              {surveyStats.recentReviews.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">🎯</div>
                  <p className="text-gray-500 text-sm">Henüz yorum yok</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-52 overflow-y-auto custom-scrollbar">
                  {surveyStats.recentReviews.map((review, index) => (
                    <div key={index} className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-3 border-l-4 border-amber-400">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex space-x-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              size={14}
                              className={`${
                                star <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-xs text-gray-500 font-medium">
                          {new Date(review.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                        </span>
                      </div>
                      <p className="text-xs text-gray-700 italic line-clamp-2 leading-relaxed">
                        "{review.feedback}"
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bu Haftanın Özeti */}
      <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-2xl shadow-lg border border-indigo-100 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
            <Calendar className="text-white" size={24} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Bu Haftanın Özeti</h3>
            <p className="text-sm text-gray-600">Son 7 günün performansı</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="text-2xl mb-2">🐕</div>
            <p className="text-sm text-gray-600 mb-1">Toplam Ziyaretçi</p>
            <p className="text-2xl font-bold text-indigo-600">{weeklySummary.totalVisitors}</p>
          </div>

          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="text-2xl mb-2">⏱️</div>
            <p className="text-sm text-gray-600 mb-1">Ortalama Kalış</p>
            <p className="text-2xl font-bold text-purple-600">{formatAverageStay(weeklySummary.averageStay)}</p>
          </div>

          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="text-2xl mb-2">📈</div>
            <p className="text-sm text-gray-600 mb-1">Değişim</p>
            <p className={`text-2xl font-bold ${weeklySummary.percentageChange >= 0 ? 'text-teal-600' : 'text-rose-600'}`}>
              {weeklySummary.percentageChange >= 0 ? '+' : ''}{weeklySummary.percentageChange}%
            </p>
          </div>
        </div>
      </div>

      {/* Başarılar ve Rekorlar */}
      <div className="bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50 rounded-2xl shadow-lg border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-md">
            <BarChart className="text-white" size={24} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">🏆 Başarılar ve Rekorlar</h3>
            <p className="text-sm text-gray-600">En iyi performanslarınız</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {achievements.map((achievement, index) => (
            <div key={index} className="bg-white rounded-xl p-5 border border-gray-200 hover:shadow-md hover:border-amber-300 transition-all duration-300">
              <div className="flex items-start space-x-3">
                <div className="text-3xl flex-shrink-0">{achievement.icon}</div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">{achievement.title}</h4>
                  <p className="text-2xl font-bold text-amber-600 mb-1">{achievement.value}</p>
                  {achievement.date && (
                    <p className="text-sm text-gray-500">{achievement.date}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Notifications */}
      {stats.pendingShares > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center mr-3">
              <AlertCircle className="text-amber-600" size={20} />
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-900">
                {stats.pendingShares} aktivite sahiplerle paylaşım bekliyor
              </p>
              <p className="text-sm text-amber-700">
                Aktiviteler bölümünden paylaşabilirsiniz.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewDashboard;
